// Wave 10 — Pre-Landings management page.
//
// Pre-landing entities (quiz / review / bonus prompt pages) sit between
// the tracker redirect and the final landing/offer. This view lists
// them, lets the operator create/edit/delete records, and exposes the
// ``redirect_method`` field so they can copy-paste the right snippet
// (302 vs meta-refresh vs JS) into their static page.

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ConfirmDialog from './ConfirmDialog';
import { toast } from 'sonner';
import { API } from '../App';
import { downloadArchive } from '../lib/downloadArchive';
import { useT } from '../i18n';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Plus, Edit, Trash2, ExternalLink, Upload, Download, Eye, FileCode, ShieldCheck } from 'lucide-react';
import BundleAudit from './BundleAudit';
import BundleFileEditor from './BundleFileEditor';
import BundlePreview, { bundleUrl, openBundle } from './BundlePreview';

const REDIRECT_METHODS = ['302', 'meta_refresh', 'js'];

const EMPTY_FORM = {
  name: '',
  url: '',
  weight: 100,
  redirect_method: '302',
  description: '',
};

export default function PreLandings() {
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    name: '',
    weight: 100,
    redirect_method: '302',
    description: '',
  });

  const reload = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/pre-landings`);
      setItems(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('Failed to load pre-landings'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name || '',
      url: row.url || '',
      weight: row.weight ?? 100,
      redirect_method: row.redirect_method || '302',
      description: row.description || '',
    });
    setDialogOpen(true);
  };

  const submit = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/pre-landings/${editing.id}`, form);
        toast.success(t('Pre-landing updated'));
      } else {
        await axios.post(`${API}/pre-landings`, form);
        toast.success(t('Pre-landing created'));
      }
      setDialogOpen(false);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('Save failed'));
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error(t('Choose a ZIP file'));
      return;
    }
    if (!uploadForm.name.trim()) {
      toast.error(t('Name is required'));
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      fd.append('name', uploadForm.name);
      fd.append('weight', String(uploadForm.weight));
      fd.append('redirect_method', uploadForm.redirect_method);
      fd.append('description', uploadForm.description);
      await axios.post(API + '/pre-landings/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(t('Pre-landing uploaded'));
      setUploadOpen(false);
      setUploadFile(null);
      setUploadForm({ name: '', weight: 100, redirect_method: '302', description: '' });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);
  // Preview, file editor and funnel check: the row or null (closed).
  const [previewItem, setPreviewItem] = useState(null);
  const [filesItem, setFilesItem] = useState(null);
  const [auditItem, setAuditItem] = useState(null);

  const remove = async (row) => {
    try {
      await axios.delete(`${API}/pre-landings/${row.id}`);
      setDeleteTarget(null);
      toast.success(t('Pre-landing deleted'));
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('Delete failed'));
    }
  };

  return (
    <div className="xp-page">
      <Card data-testid="pre-landings-card">
        <CardHeader className="flex flex-row items-start justify-between gap-2">
          <div>
            <CardTitle>{t('Pre-Landings')}</CardTitle>
            <CardDescription>
              {t(
                'Quiz / review / bonus pages that intercept the click before the final landing or offer.',
              )}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setUploadOpen(true)} data-testid="upload-pre-landing">
              <Upload className="w-4 h-4 mr-1" />
              {t('Upload ZIP')}
            </Button>
            <Button onClick={openCreate} data-testid="new-pre-landing">
              <Plus className="w-4 h-4 mr-1" />
              {t('New')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table data-testid="pre-landings-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t('Name')}</TableHead>
                <TableHead>{t('URL')}</TableHead>
                <TableHead>{t('Weight')}</TableHead>
                <TableHead>{t('Redirect')}</TableHead>
                <TableHead>{t('Visits')}</TableHead>
                <TableHead className="text-right">{t('Actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((row) => (
                <TableRow key={row.id} data-testid={`pre-landing-row-${row.id}`}>
                  <TableCell>
                    <div className="font-medium">{row.name}</div>
                    {row.is_uploaded && (
                      <Badge variant="secondary" className="mt-1">{t('Uploaded')}</Badge>
                    )}
                    {row.description && (
                      <div className="text-xs text-muted-foreground">
                        {row.description}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <a
                      href={bundleUrl(row.url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 underline"
                    >
                      {row.url}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </TableCell>
                  <TableCell>{row.weight ?? 100}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{row.redirect_method || '302'}</Badge>
                  </TableCell>
                  <TableCell>{row.total_visits ?? 0}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {row.url && (
                      <Button variant="ghost" size="sm" aria-label={t('Preview')} title={t('Preview')} onClick={() => setPreviewItem(row)} data-testid={`preview-pre-landing-${row.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    )}
                    {row.is_uploaded && (
                      <Button variant="ghost" size="sm" aria-label={t('Edit files')} title={t('Edit files')} onClick={() => setFilesItem(row)} data-testid={`files-pre-landing-${row.id}`}>
                        <FileCode className="w-4 h-4" />
                      </Button>
                    )}
                    {row.is_uploaded && (
                      <Button variant="ghost" size="sm" aria-label={t('Check funnel')} title={t('Check funnel')} onClick={() => setAuditItem({ id: row.id, name: row.name })} data-testid={`audit-pre-landing-${row.id}`}>
                        <ShieldCheck className="w-4 h-4" />
                      </Button>
                    )}
                    {row.url && (
                      <Button variant="ghost" size="sm" aria-label={t('Open pre-landing')} title={t('Open pre-landing')} onClick={() => openBundle(row.url)} data-testid={`open-pre-landing-${row.id}`}>
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t('Edit pre-landing')}
                      title={t('Edit pre-landing')}
                      onClick={() => openEdit(row)}
                      data-testid={`edit-pre-landing-${row.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    {(row.is_uploaded || row.upload_path) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={t('Download ZIP')}
                        title={t('Download ZIP')}
                        onClick={() => downloadArchive(API, `/pre-landings/${row.id}/download`, row.name).catch(() => toast.error(t('Download failed')))}
                        data-testid={`download-${row.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t('Delete pre-landing')}
                      title={t('Delete pre-landing')}
                      onClick={() => setDeleteTarget(row)}
                      data-testid={`delete-pre-landing-${row.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground">
                    {t('No pre-landings yet. Create one to start chaining traffic.')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditing(null); setForm(EMPTY_FORM); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing ? t('Edit pre-landing') : t('New pre-landing')}
            </DialogTitle>
            <DialogDescription>
              {t(
                'Operators host the static page; the tracker just routes clicks here first.',
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label>{t('Name')}</Label>
              <Input
                data-testid="pre-landing-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div>
              <Label>{t('URL')}</Label>
              <Input
                data-testid="pre-landing-url"
                value={form.url}
                onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                placeholder="https://lp.example.com/quiz"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('Weight')}</Label>
                <Input
                  data-testid="pre-landing-weight"
                  type="number"
                  min={0}
                  value={form.weight}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, weight: Number(e.target.value) || 0 }))
                  }
                />
              </div>
              <div>
                <Label>{t('Redirect method')}</Label>
                <Select
                  value={form.redirect_method}
                  onValueChange={(v) =>
                    setForm((f) => ({ ...f, redirect_method: v }))
                  }
                >
                  <SelectTrigger data-testid="pre-landing-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REDIRECT_METHODS.map((m) => (
                      <SelectItem key={m} value={m}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>{t('Description')}</Label>
              <Input
                data-testid="pre-landing-description"
                value={form.description}
                onChange={(e) =>
                  setForm((f) => ({ ...f, description: e.target.value }))
                }
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button onClick={submit} data-testid="save-pre-landing">
              {editing ? t('Save') : t('Create')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={uploadOpen} onOpenChange={(o) => { setUploadOpen(o); if (!o) setUploadFile(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('Upload pre-landing (ZIP)')}</DialogTitle>
            <DialogDescription>
              {t('Upload a ZIP with your warm-up page. index.html is auto-detected.')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label>{t('ZIP file')}</Label>
              <Input type="file" accept=".zip" data-testid="pre-landing-file"
                onChange={(e) => setUploadFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)} />
            </div>
            <div>
              <Label>{t('Name')}</Label>
              <Input value={uploadForm.name}
                onChange={(e) => setUploadForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('Weight')}</Label>
                <Input type="number" min={0} value={uploadForm.weight}
                  onChange={(e) => setUploadForm((f) => ({ ...f, weight: Number(e.target.value) || 0 }))} />
              </div>
              <div>
                <Label>{t('Redirect method')}</Label>
                <Select value={uploadForm.redirect_method}
                  onValueChange={(v) => setUploadForm((f) => ({ ...f, redirect_method: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REDIRECT_METHODS.map((mm) => (<SelectItem key={mm} value={mm}>{mm}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>{t('Description')}</Label>
              <Input value={uploadForm.description}
                onChange={(e) => setUploadForm((f) => ({ ...f, description: e.target.value }))} />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setUploadOpen(false)}>{t('Cancel')}</Button>
            <Button onClick={handleUpload} disabled={uploading} data-testid="submit-pre-landing-upload">
              {uploading ? t('Uploading…') : t('Upload')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title={t('Delete pre-landing')}
        description={t('This will permanently delete "{name}".').replace('{name}', deleteTarget?.name || '')}
        confirmLabel={t('Delete')}
        onConfirm={() => remove(deleteTarget)}
      />

      <BundlePreview item={previewItem} onClose={() => setPreviewItem(null)} openLabel={t('Open pre-landing')} />
      <BundleFileEditor kind="pre-landings" item={filesItem} onClose={() => setFilesItem(null)} />
      <BundleAudit kind="pre-landings" item={auditItem} onClose={() => setAuditItem(null)} />
    </div>
  );
}
